/* [VALE] Product photo gallery lightbox.
   Custom element for sections/product-photo-gallery.liquid. Vanilla — no theme
   JS dependency, so an Ignite upgrade can't unwire it, and nothing here touches
   the buy-box media gallery.

   Behaviour: click a tile -> full-size photo in a <dialog>; arrows / swipe /
   keyboard to move; Esc or backdrop click to close; focus returns to the tile
   that opened it. Neighbouring photos are preloaded, the rest are not. */
(function () {
  'use strict';

  class ProductPhotoGallery extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.tiles = Array.from(this.querySelectorAll('[data-ppg-open]'));
      this.dialog = this.querySelector('[data-ppg-lightbox]');
      if (!this.tiles.length || !this.dialog) return;

      this.img = this.dialog.querySelector('[data-ppg-img]');
      this.caption = this.dialog.querySelector('[data-ppg-caption]');
      this.counter = this.dialog.querySelector('[data-ppg-counter]');
      this.prevBtn = this.dialog.querySelector('[data-ppg-prev]');
      this.nextBtn = this.dialog.querySelector('[data-ppg-next]');
      this.index = 0;
      this.opener = null;
      this.supportsDialog = typeof this.dialog.showModal === 'function';

      // The tile is the source of truth for the full-size URL and alt text, so
      // the slide list survives a section re-render in the theme editor.
      this.tiles.forEach((tile, i) => {
        tile.addEventListener('click', () => this.open(i));
      });

      this.querySelectorAll('[data-ppg-more]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.querySelectorAll('.ppg__item--extra').forEach((li) => li.removeAttribute('hidden'));
          btn.closest('.ppg__more').remove();
        });
      });

      this.dialog.querySelector('[data-ppg-close]').addEventListener('click', () => this.close());
      this.prevBtn.addEventListener('click', () => this.go(-1));
      this.nextBtn.addEventListener('click', () => this.go(1));

      // Backdrop click: a click on the dialog itself (not the figure) closes.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.classList.contains('ppg__lb-inner')) this.close();
      });

      this.dialog.addEventListener('cancel', () => this.close());

      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
        else if (e.key === 'Escape' && !this.supportsDialog) { e.preventDefault(); this.close(); }
      });

      this._touchX = null;
      this.dialog.addEventListener('touchstart', (e) => {
        this._touchX = e.changedTouches[0].clientX;
      }, { passive: true });
      this.dialog.addEventListener('touchend', (e) => {
        if (this._touchX === null) return;
        const dx = e.changedTouches[0].clientX - this._touchX;
        this._touchX = null;
        if (Math.abs(dx) > 45) this.go(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    open(i) {
      this.opener = this.tiles[i];
      this.show(i);
      if (this.supportsDialog) {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('data-ppg-fallback-open', '');
        this._escHandler = (e) => { if (e.key === 'Escape') this.close(); };
        document.addEventListener('keydown', this._escHandler);
      }
      document.documentElement.style.overflow = 'hidden';
      this.dialog.querySelector('[data-ppg-close]').focus();
    }

    close() {
      if (this.supportsDialog) {
        if (this.dialog.open) this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.dialog.removeAttribute('data-ppg-fallback-open');
        document.removeEventListener('keydown', this._escHandler);
      }
      document.documentElement.style.overflow = '';
      if (this.opener) this.opener.focus();
    }

    go(step) {
      const n = this.tiles.length;
      this.show((this.index + step + n) % n);
    }

    show(i) {
      const tile = this.tiles[i];
      if (!tile) return;
      this.index = i;
      this.opener = tile;

      const alt = tile.getAttribute('data-ppg-alt') || '';
      this.img.src = tile.getAttribute('data-ppg-full');
      this.img.alt = alt;
      this.caption.textContent = alt;
      this.counter.textContent = (i + 1) + ' / ' + this.tiles.length;

      const single = this.tiles.length < 2;
      this.prevBtn.hidden = single;
      this.nextBtn.hidden = single;

      this.preload(i + 1);
      this.preload(i - 1);
    }

    preload(i) {
      const n = this.tiles.length;
      const tile = this.tiles[(i + n) % n];
      if (!tile) return;
      const url = tile.getAttribute('data-ppg-full');
      if (!url) return;
      this._preloaded = this._preloaded || new Set();
      if (this._preloaded.has(url)) return;
      this._preloaded.add(url);
      const img = new Image();
      img.src = url;
    }
  }

  if (!customElements.get('product-photo-gallery')) {
    customElements.define('product-photo-gallery', ProductPhotoGallery);
  }
})();
